import React, { useEffect } from "react";
import "~/app.scss";
import { useNavigate, Routes, Route } from "react-router-dom";
import PageNotFound from "~/pages/PageNotFound";
import authStore from "~/stores/auth";
import { generatePkceAuthUrl } from "~/auth/pkce";
import { observer } from "mobx-react";
import { useNetworkErrorHandle, useQuery } from "~/util/hooks";
import Nav from "~/components/Nav";
import DocumentsPage from "~/pages/Documents";
import BulkDownloadModal from "~/components/BulkDownloaderModal";
import Footer from "~/components/Footer";
import AdvancedSearchPage from "~/pages/AdvancedSearchPage";
import UploadPage from "~/pages/UploadPage";
import paths from "~/paths";
import DocumentDetailPage, { DocumentDetailPageRedirect } from "~/pages/DocumentDetailPage";
import { Spinner } from "~/components/ui/spinner";
import PersonalArea from "~/pages/PersonalArea";
import AppContext from "./AppContext";
import { configure } from "mobx";
import { clarity } from "react-microsoft-clarity";
import SearchRedirect from "~/pages/SearchRedirect";

// Initialize Clarity only if the ID is available
const clarityId = (window as any).__CLARITY_ID__ || import.meta.env.VITE_CLARITY_ID;
if (clarityId) {
  try {
    clarity.init(clarityId);
  } catch (e) {
    console.warn("Failed to initialize Clarity:", e);
  }
}

// batch mobx state updates by default
setTimeout(() => {
  configure({
    enforceActions: "never",
    reactionScheduler: (f) => setTimeout(f),
  });
});

const currPath = () => {
  if (window.location.href) {
    return window.location.href.substring(window.location.origin.length);
  }
  return "";
};

const AuthCallbackPage = () => {
  const search = useQuery();
  const navigate = useNavigate();
  useEffect(() => {
    (async () => {
      try {
        await authStore.loginWithCode(search.code);
        // after successful login, navigate to the previously accessed page or the index page
        const lastAccessedPath = localStorage.getItem(LAST_ACCESSED_PATH_BEFORE_AUTH) ?? "/";
        // Clean up before navigating to avoid timing issues
        localStorage.removeItem(LAST_ACCESSED_PATH_BEFORE_AUTH);
        // Use replace to prevent back navigation to callback page
        navigate(lastAccessedPath, { replace: true });
      } catch (err) {
        // On error, still clean up and redirect to home
        localStorage.removeItem(LAST_ACCESSED_PATH_BEFORE_AUTH);
        navigate("/", { replace: true });
      }
    })();
  }, [search.code]);

  return <div>Logging in...</div>;
};

const LAST_ACCESSED_PATH_BEFORE_AUTH = "last-accessed-path-before-auth";

// Navigate to the auth provider's interactive login page
const goToLoginPage = async () => {
  // Save the current URL before going to the auth provider page so that we can restore it when the authentication is completed
  const currentPath = currPath();
  localStorage.setItem(LAST_ACCESSED_PATH_BEFORE_AUTH, currentPath);

  const link = await generatePkceAuthUrl();
  window.location.assign(link);
};

const LoggedInPage = observer(() => {
  useNetworkErrorHandle();
  useEffect(() => {
    if (!authStore.loggedIn) {
      goToLoginPage();
    } else {
      authStore.fetchUserInfo();
    }
  }, [authStore.loggedIn]);

  useEffect(() => {
    if (clarity.hasStarted() && authStore.userInfo?.user) {
      clarity.identify("USER_ID", {
        userProperty: authStore.userInfo.user,
      });
    }
  }, [authStore.userInfo?.user]);

  if (!authStore.loggedIn) {
    return null;
  }

  return (
    <div className="App">
      <Nav />
      <div style={{ marginTop: 87 }}>
        {authStore.loading ? (
          <div className="flex h-full w-full py-4 items-center justify-center">
            <Spinner className="size-6" />
          </div>
        ) : (
          <Routes>
            <Route path="/advanced-search/" element={<AdvancedSearchPage />} />
            <Route path={paths.match.documents} element={<DocumentsPage />} />
            <Route path={paths.match.browse} element={<DocumentsPage />} />
            <Route path="/search/detail/:id" element={<DocumentDetailPageRedirect />} />
            <Route path={paths.match.searchRedirect} element={<SearchRedirect />} />
            <Route path={paths.match.docDetail} element={<DocumentDetailPage />} />
            <Route path={paths.match.personalArea} element={<PersonalArea />} />
            <Route path="/upload/*" element={<UploadPage />} />
            <Route path={paths.match.editDocument} element={<UploadPage />} />
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        )}
        <BulkDownloadModal />
        <Footer />
      </div>
    </div>
  );
});

const App = observer(() => {
  return (
    <AppContext>
      <Routes>
        <Route path="/callback" element={<AuthCallbackPage />} />
        <Route path="/oidc/done" element={<AuthCallbackPage />} />
        <Route path="*" element={<LoggedInPage />} />
      </Routes>
    </AppContext>
  );
});

export default App;
